"use client";

import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";
import type { ZodSchema } from "zod";

import {
  type LocalStorageValidator,
  getLocalStorageSnapshot,
  getLocalStorageValue,
  isLocalStorageAvailable,
  parseStoredValue,
  setLocalStorageValue,
  subscribeLocalStorage,
} from "../lib/local-storage";
export {
  isStringArray,
  isBooleanRecord,
  parseLocalStorageValue,
  type LocalStorageValidator,
} from "../lib/local-storage";

const getServerSnapshot = () => null;
export type UseLocalStorageOptions<T> = {
  validate?: LocalStorageValidator<T>;
};

function isZodSchema(value: unknown): value is ZodSchema<unknown> {
  return (
    value != null &&
    typeof value === "object" &&
    "parse" in value &&
    typeof value.parse === "function" &&
    "safeParse" in value &&
    typeof value.safeParse === "function"
  );
}

function jsonIdentity(value: unknown): unknown {
  try {
    return JSON.stringify(value) ?? value;
  } catch {
    return value;
  }
}

/**
 * Read and write validated localStorage state without letting storage failures
 * escape into user interactions. Storage is the source of truth, so mounted
 * consumers and browser tabs stay synchronized. Functional setters re-read at
 * call time to avoid overwriting a newer value from another consumer.
 *
 * The schema overload preserves the original shared-hook API. The options
 * overload is useful when callers already have a lightweight type guard.
 */
export function useLocalStorage<T>(
  key: string,
  schema: ZodSchema<T>,
  defaultValue: T,
): readonly [T, Dispatch<SetStateAction<T>>];
export function useLocalStorage<T>(
  key: string,
  defaultValue: T,
  options?: UseLocalStorageOptions<T>,
): readonly [T, Dispatch<SetStateAction<T>>];

export function useLocalStorage<T>(
  key: string,
  schemaOrDefault: ZodSchema<T> | T,
  defaultOrOptions?: T | UseLocalStorageOptions<T>,
): readonly [T, Dispatch<SetStateAction<T>>] {
  const schema = isZodSchema(schemaOrDefault)
    ? (schemaOrDefault as ZodSchema<T>)
    : undefined;
  const defaultValue = (
    schema == null ? schemaOrDefault : defaultOrOptions
  ) as T;
  const validate =
    schema == null
      ? (defaultOrOptions as UseLocalStorageOptions<T> | undefined)?.validate
      : undefined;

  // Defaults are commonly array/object literals or derived collections. Give
  // equivalent JSON values a stable reference so parsing stored JSON does not
  // manufacture a new result, and retrigger consumers' effects, on every
  // unrelated render. A genuinely changed default still takes effect.
  const defaultValueIdentity = jsonIdentity(defaultValue);
  const stableDefaultValueRef = useRef({
    identity: defaultValueIdentity,
    value: defaultValue,
  });

  if (
    !Object.is(stableDefaultValueRef.current.identity, defaultValueIdentity)
  ) {
    stableDefaultValueRef.current = {
      identity: defaultValueIdentity,
      value: defaultValue,
    };
  }

  const stableDefaultValue = stableDefaultValueRef.current.value;

  const getSnapshot = useCallback(() => getLocalStorageSnapshot(key), [key]);
  const subscribe = useCallback(
    (onStoreChange: () => void) => subscribeLocalStorage(key, onStoreChange),
    [key],
  );
  const raw = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const value = useMemo(
    () => parseStoredValue(raw, stableDefaultValue, validate, schema),
    [raw, stableDefaultValue, validate, schema],
  );

  const defaultValueRef = useRef(defaultValue);
  defaultValueRef.current = defaultValue;
  const validatorRef = useRef(validate);
  validatorRef.current = validate;
  const schemaRef = useRef(schema);
  schemaRef.current = schema;

  const setValue = useCallback<Dispatch<SetStateAction<T>>>(
    (action) => {
      if (!isLocalStorageAvailable()) {
        return;
      }

      const current = getLocalStorageValue(key, defaultValueRef.current, {
        validate: validatorRef.current,
        schema: schemaRef.current,
      });
      const next =
        typeof action === "function"
          ? (action as (previous: T) => T)(current)
          : action;

      setLocalStorageValue(key, next);
    },
    [key],
  );

  return [value, setValue] as const;
}
